import { config as configureZod, type core } from 'zod';
import { I18n, type TranslateOptions } from 'i18n-js';
import { useMemo, useSyncExternalStore } from 'react';
import { localePickerOptions } from '@rhino-project/core/i18n/selection';

export type TranslationCatalog = Record<string, Record<string, unknown>>;
export interface TranslationManifest {
  defaultLocale: string;
  locales: string[];
  /** Languages with frontend copy to offer in the UI; all locales remain loadable. */
  selectableLocales?: string[];
  /** Display-only equivalents; matching, catalogs and saved preferences stay intact. */
  localePickerAliases?: Record<string, string>;
  /** Unambiguous IANA time zones for regions with frontend translations. */
  timeZoneRegions?: Record<string, string>;
  fallbacks: Record<string, string[]>;
}
export type TranslationLoaders = Record<
  string,
  () => Promise<TranslationCatalog>
>;

export type ValidationLoaders = Record<
  string,
  () => Promise<{ localeError: core.$ZodErrorMap }>
>;

/** Portable runtime: the browser's Vite loader and native can supply catalogs. */
export class TranslationRuntime {
  readonly i18n = new I18n();
  manifest: TranslationManifest = {
    defaultLocale: 'en',
    locales: ['en'],
    fallbacks: { en: ['en'] }
  };
  private loaders: TranslationLoaders = {};
  private pending = new Map<string, Promise<TranslationCatalog>>();
  private request = 0;
  private validationLoaders: ValidationLoaders = {};

  configure(
    manifest: TranslationManifest,
    loaders: TranslationLoaders,
    locale = this.i18n.locale,
    validationLoaders: ValidationLoaders = {}
  ) {
    this.manifest = manifest;
    this.validationLoaders = validationLoaders;
    this.loaders = loaders;
    this.pending = new Map();
    return this.setLocale(
      manifest.locales.includes(locale) ? locale : manifest.defaultLocale
    );
  }

  private load(locale: string) {
    let pending = this.pending.get(locale);
    if (!pending) {
      const loader = this.loaders[locale];
      if (!loader)
        return Promise.reject(
          new RangeError(`No catalog for locale: ${locale}`)
        );
      const cache = this.pending;
      pending = loader().catch((error: unknown) => {
        cache.delete(locale);
        throw error;
      });
      cache.set(locale, pending);
    }
    return pending;
  }

  async setLocale(locale: string) {
    if (!this.manifest.locales.includes(locale))
      throw new RangeError(`Unsupported locale: ${locale}`);
    const request = ++this.request;
    const chain = this.manifest.fallbacks[locale] ?? [
      locale,
      this.manifest.defaultLocale
    ];
    const [catalogs, validation] = await Promise.all([
      Promise.all(
        [...new Set([locale, ...chain])].map((name) => this.load(name))
      ),
      this.validationLoaders[locale]?.()
    ]);
    if (request !== this.request) return;

    // Replace rather than deep-merge: deleting a key during HMR must remove it.
    this.i18n.translations = Object.assign({}, ...catalogs);
    this.i18n.defaultLocale = this.manifest.defaultLocale;
    this.i18n.locales.register(locale, [locale, ...chain]);
    this.i18n.pluralization.register(locale, (_i18n, count) => {
      const category = new Intl.PluralRules(locale.replaceAll('_', '-')).select(
        count
      );
      return count === 0 ? ['zero', category] : [category];
    });
    if (validation) configureZod(validation);
    this.i18n.locale = locale;
    this.i18n.store({});
  }

  t = (key: string, options?: TranslateOptions): string =>
    this.i18n.t(key, options);
  subscribe = (listener: () => void) => this.i18n.onChange(listener);
  snapshot = () => this.i18n.version;
}

export const translations = new TranslationRuntime();
export const translate = translations.t;
export const getLocale = () => translations.i18n.locale;
const localeSelections = new Set<(locale: string) => void>();

/** Explicit user choices; bootstrap and catalog HMR do not save preferences. */
export const onLocaleSelected = (listener: (locale: string) => void) => {
  localeSelections.add(listener);
  return () => localeSelections.delete(listener);
};
export const setLocale = async (locale: string) => {
  await translations.setLocale(locale);
  if (getLocale() === locale)
    localeSelections.forEach((listener) => listener(locale));
};

export function useTranslation() {
  const version = useSyncExternalStore(
    translations.subscribe,
    translations.snapshot,
    translations.snapshot
  );
  return useMemo(
    () => ({
      t: (key: string, options?: TranslateOptions) => translate(key, options),
      locale: getLocale(),
      availableLocales:
        translations.manifest.selectableLocales ??
        translations.manifest.locales,
      ...localePickerOptions(
        getLocale(),
        translations.manifest.selectableLocales ??
          translations.manifest.locales,
        translations.manifest.localePickerAliases
      ),
      setLocale,
      version
    }),
    [version]
  );
}

export const modelLabel = (
  model: { model: string; readableName: string; pluralReadableName?: string },
  count = 1
) =>
  translate(`rhino.generated.${model.model}.${count === 1 ? 'one' : 'other'}`, {
    defaultValue:
      count === 1
        ? model.readableName
        : (model.pluralReadableName ?? model.readableName)
  });
